import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { Panel, Field, NativeSelect } from "@/components/kit";
import { MfaEnroll } from "@/components/MfaSetup";
import { errMsg } from "@/lib/format";

const Live = ({ env }) => { const { t } = useApp(); return <span className={`ms-1 rounded px-1.5 text-[10px] font-bold ${env === "production" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`} data-testid="op-mode-badge">{t(env === "production" ? "op_live" : "op_sandbox")}</span>; };
const Sim = () => { const { t } = useApp(); return <span className="ms-1 rounded bg-orange-100 px-1.5 text-[10px] font-bold text-orange-800">{t("simulated")}</span>; };

export const OnlinePaymentPanel = ({ rw }) => {
  const { api, t, company, refreshCompany } = useApp();
  const [f, setF] = useState(null);
  useEffect(() => {
    const op = company.online_payment || {};
    setF({ enabled: !!op.enabled, provider: op.provider || "konnect", environment: op.environment || "test", konnect_api_key: op.konnect?.api_key || "", konnect_wallet_id: op.konnect?.wallet_id || "",
      flouci_app_token: op.flouci?.app_token || "", flouci_app_secret: op.flouci?.app_secret || "" });
  }, [company]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.put("/company/online-payment", f); await refreshCompany(); toast.success(t("saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  const keys = f.provider === "konnect" ? [["konnect_api_key", "API key"], ["konnect_wallet_id", "Wallet ID"]] : [["flouci_app_token", "App token"], ["flouci_app_secret", "App secret"]];
  return (
    <Panel title={<span>{t("online_payment")}{company.online_payment?.mode === "live" ? <Live env={company.online_payment.environment} /> : <Sim />}</span>} testid="settings-online-payment">
      <div className="space-y-3">
        <label className="flex items-center gap-3 text-sm"><Switch checked={f.enabled} disabled={!rw} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="op-enabled" />{t("op_enable")}</label>
        <Field label={t("op_provider")}>
          <NativeSelect value={f.provider} onChange={set("provider")} disabled={!rw} data-testid="op-provider"><option value="konnect">Konnect</option><option value="flouci">Flouci</option></NativeSelect>
        </Field>
        <Field label={t("op_environment")}>
          <NativeSelect value={f.environment} onChange={set("environment")} disabled={!rw} data-testid="op-environment"><option value="test">{t("op_sandbox")}</option><option value="production">{t("op_live")}</option></NativeSelect>
        </Field>
        {keys.map(([k, l]) => <Field key={k} label={l}><Input value={f[k]} onChange={set(k)} disabled={!rw} dir="ltr" autoComplete="off" data-testid={`op-${k}`} /></Field>)}
        <p className="text-xs text-slate-500">{t("op_help")}</p>
        <p className="text-xs text-slate-500" data-testid="op-webhook">{t("op_webhook")} <span className="num" dir="ltr">{window.location.origin}/api/webhooks/{f.provider}</span></p>
        {rw && <Button variant="outline" onClick={save} data-testid="op-save">{t("save")}</Button>}
      </div>
    </Panel>
  );
};

export const SmsPanel = ({ s, setS, rw }) => {
  const { t } = useApp();
  return (
    <Panel title={<span>{t("sms_reminders")}<Sim /></span>} testid="settings-sms">
      <label className="flex items-center gap-3 text-sm"><Switch checked={s.sms_reminders !== false} disabled={!rw} onCheckedChange={(v) => setS("sms_reminders", v)} data-testid="settings-sms-reminders" />{t("sms_auto")}</label>
      <p className="mt-2 text-xs text-slate-500">{t("sms_help")}</p>
    </Panel>
  );
};

export const ReceiptReminderPanel = ({ s, setS, rw }) => {
  const { t } = useApp();
  return (
    <Panel title={t("receipt_reminders")} testid="settings-receipt-reminders-panel">
      <label className="flex items-center gap-3 text-sm"><Switch checked={s.receipt_reminders !== false} disabled={!rw} onCheckedChange={(v) => setS("receipt_reminders", v)} data-testid="settings-receipt-reminders" />{t("receipt_reminders_auto")}</label>
    </Panel>
  );
};

export const SecurityPanel = () => {
  const { t, me } = useApp();
  const [open, setOpen] = useState(false);
  const m = me?.mfa || {};
  const available = (m.available || []).some((x) => x === "totp" || x === "phone_code");
  return (
    <Panel title={t("security")} testid="settings-security">
      <div className="flex items-start gap-3">
        {m.enrolled ? <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 shrink-0 text-orange-600" />}
        <div className="space-y-1 text-sm">
          <p className="font-medium" data-testid="mfa-status">{m.enrolled ? t("mfa_on") : t("mfa_off")}</p>
          <p className="text-xs text-slate-500">{m.required ? t("mfa_required_note") : t("mfa_optional_note")}</p>
          {!available && <p className="text-xs text-orange-700" data-testid="mfa-pending">{t("mfa_pending")}</p>}
        </div>
      </div>
      {available && !m.enrolled && <Button className="mt-3" variant="outline" onClick={() => setOpen(true)} data-testid="mfa-open">{t("mfa_enable")}</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white"><DialogHeader><DialogTitle>{t("mfa_enable")}</DialogTitle></DialogHeader><MfaEnroll onDone={() => setOpen(false)} /></DialogContent>
      </Dialog>
    </Panel>
  );
};
